import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  orderBy,
  where,
  limit,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';

export interface UserDoc {
  id: string;
  role?: string;
  phone?: string;
  displayName?: string;
  classes?: string[];
  email?: string;
  reportBlocked?: boolean;
  createdAt?: unknown;
  [key: string]: unknown;
}

const COLLECTION = 'users';

export async function getUserDoc(uid: string): Promise<UserDoc | null> {
  const snap = await getDoc(doc(db, COLLECTION, uid));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as UserDoc;
}

const ALL_USERS_LIMIT = 200;

export async function getAllUsers(): Promise<UserDoc[]> {
  const q = query(collection(db, COLLECTION), orderBy('createdAt', 'desc'), limit(ALL_USERS_LIMIT));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as UserDoc);
}

export async function updateUserRole(uid: string, newRole: string): Promise<void> {
  const fn = httpsCallable(functions, 'updateStaffRole');
  await fn({ targetUid: uid, newRole });
}

export async function updateDisplayName(uid: string, displayName: string): Promise<void> {
  await updateDoc(doc(db, COLLECTION, uid), { displayName });
}

export async function setReportBlocked(uid: string, blocked: boolean): Promise<void> {
  await updateDoc(doc(db, COLLECTION, uid), { reportBlocked: !!blocked });
}

export async function deleteStaffUser(uid: string): Promise<void> {
  const fn = httpsCallable(functions, 'deleteStaffUser');
  await fn({ targetUid: uid });
}

let _displayMapCache: Record<string, string> | null = null;
let _displayMapCacheTime = 0;
const DISPLAY_MAP_TTL = 5 * 60 * 1000;

export function clearDisplayMapCache(): void {
  _displayMapCache = null;
  _displayMapCacheTime = 0;
}

const USER_DISPLAY_MAP_LIMIT = 500;

export async function getUserDisplayMap(): Promise<Record<string, string>> {
  const now = Date.now();
  if (_displayMapCache && now - _displayMapCacheTime < DISPLAY_MAP_TTL) {
    return _displayMapCache;
  }
  const q = query(collection(db, COLLECTION), orderBy('createdAt', 'desc'), limit(USER_DISPLAY_MAP_LIMIT));
  const snap = await getDocs(q);
  const map: Record<string, string> = {};
  snap.docs.forEach((d) => {
    const data = d.data();
    map[d.id] = data.displayName || data.email || d.id;
  });
  _displayMapCache = map;
  _displayMapCacheTime = now;
  return map;
}

export async function getReportBlockedUids(): Promise<Set<string>> {
  const q = query(
    collection(db, COLLECTION),
    where('reportBlocked', '==', true),
    limit(USER_DISPLAY_MAP_LIMIT),
  );
  const snap = await getDocs(q);
  return new Set(snap.docs.map((d) => d.id));
}

export async function createStaffUser(
  email: string,
  password: string,
  role: string,
  displayName: string,
): Promise<{ uid: string }> {
  const fn = httpsCallable(functions, 'createStaffUser');
  const result = await fn({ email, password, role, displayName });
  return result.data as { uid: string };
}
